import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Fragment } from "react";

import { SidebarBranding } from "./SidebarBranding";
import { SidebarDashboardNavLink } from "./SidebarDashboardNavLink";
import { SidebarMenu } from "./SidebarMenu";
import { useSidebarState } from "./useSidebarState";

import { Button } from "@/components/Button";

type SidebarProps = {
  collapsed?: boolean;
  isMobileMenuOpen?: boolean;
  onMobileMenuClose?: () => void;
};

export function Sidebar({
  collapsed = false,
  isMobileMenuOpen = false,
  onMobileMenuClose,
}: SidebarProps) {
  const {
    branding,
    homeHref,
    dashboardItem,
    shouldPinDashboard,
    isDashboardActive,
    moduleGroups,
    expandedSections,
    sidebarWidthClass,
    toggleSection,
    onLogoUrlChange,
  } = useSidebarState(collapsed);

  return (
    <>
      {/* Desktop */}
      <aside
        aria-label="Main navigation"
        className={clsx(
          "hidden lg:flex flex-col fixed left-0 z-40",
          "top-[var(--platform-banner-h,0px)] h-[calc(100vh-var(--platform-banner-h,0px))]",
          sidebarWidthClass,
          "bg-brand-sidebar-bg dark:bg-slate-950",
          "border-r border-slate-200 dark:border-slate-800",
          "transition-[width] duration-200",
        )}
      >
        <SidebarBranding
          branding={branding}
          collapsed={collapsed}
          onLogoUrlChange={onLogoUrlChange}
          homeHref={homeHref}
        />

        <div
          className={clsx(
            "app-themed-scroll flex-1 overflow-y-auto",
            collapsed ? "py-2" : "py-3",
          )}
        >
          {shouldPinDashboard && dashboardItem && (
            <div className={clsx(collapsed ? "px-2" : "px-3", "mb-2")}>
              <SidebarDashboardNavLink
                item={dashboardItem}
                collapsed={collapsed}
                isActive={isDashboardActive}
              />
            </div>
          )}
          <SidebarMenu
            groups={moduleGroups}
            collapsed={collapsed}
            expanded={expandedSections}
            onToggleGroup={toggleSection}
            onItemClick={onMobileMenuClose}
          />
        </div>
      </aside>

      {/* Mobile */}
      <Transition show={isMobileMenuOpen} as={Fragment}>
        <Dialog
          className="relative z-50 lg:hidden"
          aria-label="Navigation"
          onClose={() => onMobileMenuClose?.()}
          data-workspace-overlay
        >
          <TransitionChild
            as={Fragment}
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/25" />
          </TransitionChild>
          <TransitionChild
            as={Fragment}
            enter="transition-transform duration-200"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition-transform duration-200"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <DialogPanel
              as="aside"
              className={clsx(
                "fixed left-0 bottom-0 z-50 flex w-72 flex-col bg-brand-sidebar-bg dark:bg-slate-950",
                "top-[var(--platform-banner-h,0px)]",
                "border-r border-slate-200 dark:border-slate-800",
                "lg:hidden",
              )}
              aria-label="Mobile navigation"
            >
              <SidebarBranding
                branding={branding}
                collapsed={false}
                onLogoUrlChange={onLogoUrlChange}
                rightSlot={
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onMobileMenuClose?.()}
                    aria-label="Close menu"
                    className="p-2 rounded-md"
                  >
                    <XMarkIcon className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                  </Button>
                }
              />

              <div className="app-themed-scroll min-h-0 flex-1 overflow-y-auto py-3">
                {shouldPinDashboard && dashboardItem && (
                  <div className="px-3 mb-2">
                    <SidebarDashboardNavLink
                      item={dashboardItem}
                      collapsed={false}
                      isActive={isDashboardActive}
                    />
                  </div>
                )}
                <SidebarMenu
                  groups={moduleGroups}
                  collapsed={false}
                  expanded={expandedSections}
                  onToggleGroup={toggleSection}
                  onItemClick={onMobileMenuClose}
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </>
  );
}

export default Sidebar;
